import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { MessagesSkeleton } from '../../components/Skeleton'
import {
  ContentMaxWidth,
  Muted,
  PageHeading,
  PageSubheading,
  Pill,
  Row,
  Stack,
} from '../../layout/styledComponents'
import { usePatient } from '../../context/PatientContext'
import * as svc from '../../services/messagesService'
import { parseUtcDate } from '../../utils/dates'

const ThreadRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  border-radius: 16px;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  &:hover {
    border-color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  }
`

const Avatar = styled.span`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${(p) =>
    p.$type === 'SUPPORT'
      ? p.theme?.color?.accent || '#6B9080'
      : p.theme?.color?.primary || '#1F3A5F'};
  color: white;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  letter-spacing: 0.05em;
  flex-shrink: 0;
`

const Body = styled.div`
  flex: 1;
  min-width: 0;
`

const NameLine = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`

const Name = styled.span`
  font-weight: 600;
  font-size: 15px;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const When = styled.span`
  font-size: 11px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  flex-shrink: 0;
`

const Preview = styled.div`
  font-size: 13px;
  color: ${(p) => (p.$bold ? p.theme?.color?.ink : p.theme?.color?.inkMuted)};
  font-weight: ${(p) => (p.$bold ? 600 : 400)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
`

const UnreadBadge = styled.span`
  background: ${(p) => p.theme?.color?.urgent || '#C24D49'};
  color: white;
  border-radius: 999px;
  padding: 0 8px;
  height: 20px;
  min-width: 20px;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

function initials(name) {
  if (!name) return '?'
  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

function whenLabel(iso) {
  if (!iso) return ''
  const d = parseUtcDate(iso)
  const now = new Date()
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  }
  const diffDays = Math.floor((now - d) / 86400000)
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' })
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

export default function MessagesIndexPage() {
  const { currentProfile } = usePatient()
  const navigate = useNavigate()
  const [threads, setThreads] = useState(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    if (!currentProfile) return
    setLoading(true)
    try {
      const list = await svc.listThreads(currentProfile)
      setThreads(list)
    } finally {
      setLoading(false)
    }
  }, [currentProfile])

  useEffect(() => {
    setThreads(null)
    refresh()
  }, [refresh])

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <div>
          <Pill>Messages</Pill>
          <PageHeading style={{ marginTop: 12 }}>Messages</PageHeading>
          <PageSubheading>Practitioners and support — all in one inbox.</PageSubheading>
        </div>

        {loading && !threads ? (
          <MessagesSkeleton />
        ) : !threads?.length ? (
          <div
            style={{
              padding: '48px 24px',
              textAlign: 'center',
              border: '1px dashed rgba(31,41,55,0.18)',
              borderRadius: 20,
            }}
          >
            <p style={{ fontSize: 18, marginTop: 0, marginBottom: 8 }}>No conversations yet.</p>
            <Muted>Threads with your practitioners and support land here.</Muted>
          </div>
        ) : (
          <Stack $gap="10px">
            {threads.map((t) => (
              <ThreadRow
                key={t.peerId}
                onClick={() => navigate(`/patient-home/messages/${t.peerId}`)}
              >
                <Avatar $type={t.peerType}>{initials(t.peerName)}</Avatar>
                <Body>
                  <NameLine>
                    <Name>
                      {t.pinned ? '📌 ' : ''}
                      {t.peerName || t.peerId}
                    </Name>
                    <Row $gap="6px">
                      {t.unreadCount > 0 && <UnreadBadge>{t.unreadCount}</UnreadBadge>}
                      <When>{whenLabel(t.lastMessageAt)}</When>
                    </Row>
                  </NameLine>
                  <Preview $bold={t.unreadCount > 0}>{t.lastMessagePreview || 'No messages yet'}</Preview>
                  {t.availabilityHint && (
                    <Muted style={{ fontSize: 11, marginTop: 2 }}>{t.availabilityHint}</Muted>
                  )}
                </Body>
              </ThreadRow>
            ))}
          </Stack>
        )}
      </Stack>
    </ContentMaxWidth>
  )
}
